import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, FileText, FileSignature, TrendingUp, UserPlus, FilePlus, Filter, DollarSign, CheckCircle, Clock, AlertTriangle, Calendar, Wallet, ArrowUpCircle, ArrowDownCircle, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart, Line } from 'recharts';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// Helper functions for date conversion
const isoToDateBR = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

const dateBRToIso = (dateBR) => {
  if (!dateBR || dateBR.length !== 10) return '';
  const [day, month, year] = dateBR.split('/');
  if (!day || !month || !year) return '';
  return `${year}-${month}-${day}`;
};

const formatDateInput = (value) => {
  // Remove non-digits
  const digits = value.replace(/\D/g, '');
  
  // Apply mask DD/MM/YYYY
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Custom Tooltip Components (defined outside to avoid re-render issues)
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CashFlowTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200 min-w-[200px]">
        <p className="font-semibold text-gray-900 mb-3 border-b pb-2">{label}</p>
        <div className="space-y-2 text-sm">
          <p className="text-gray-600">
            Saldo Inicial: <span className="font-medium text-gray-900">{formatCurrency(data?.opening_balance || 0)}</span>
          </p>
          <p className="text-green-600">
            + Entradas: <span className="font-medium">{formatCurrency(data?.income || 0)}</span>
          </p>
          <p className="text-red-600">
            - Saídas: <span className="font-medium">{formatCurrency(data?.expenses || 0)}</span>
          </p>
          <div className="border-t pt-2 mt-2">
            <p className={`font-semibold ${(data?.closing_balance || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              Saldo Final: {formatCurrency(data?.closing_balance || 0)}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const AdminDashboard = () => {
  const { admin, token } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [cashFlowLoading, setCashFlowLoading] = useState(true);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'yearly'
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  
  // Custom date range (store in ISO format internally)
  const today = new Date();
  const defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const defaultEndDate = new Date(today.getFullYear(), today.getMonth() + 6, 0).toISOString().split('T')[0];
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  
  // Display values in BR format
  const [startDateDisplay, setStartDateDisplay] = useState(isoToDateBR(defaultStartDate));
  const [endDateDisplay, setEndDateDisplay] = useState(isoToDateBR(defaultEndDate));

  const handleStartDateChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setStartDateDisplay(formatted);
    if (formatted.length === 10) {
      const iso = dateBRToIso(formatted);
      if (iso) setStartDate(iso);
    }
  };

  const handleEndDateChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setEndDateDisplay(formatted);
    if (formatted.length === 10) {
      const iso = dateBRToIso(formatted);
      if (iso) setEndDate(iso);
    }
  };

  // Generate year options (current year - 2 to current year + 2)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear - 2; y <= currentYear + 2; y++) {
    yearOptions.push(y);
  }

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      let url = `${API}/admin/stats/contracts-chart?`;
      
      if (viewMode === 'yearly') {
        url += `year=${yearFilter}&view=yearly`;
      } else {
        url += `start_date=${startDate}&end_date=${endDate}&view=custom`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartData(response.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setChartLoading(false);
    }
  }, [token, viewMode, yearFilter, startDate, endDate]);

  const fetchCashFlowData = useCallback(async () => {
    setCashFlowLoading(true);
    try {
      let url = `${API}/admin/cash-flow?`;
      
      if (viewMode === 'yearly') {
        url += `year=${yearFilter}`;
      } else {
        url += `start_date=${startDate}&end_date=${endDate}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCashFlowData(response.data);
    } catch (error) {
      console.error('Error fetching cash flow data:', error);
    } finally {
      setCashFlowLoading(false);
    }
  }, [token, viewMode, yearFilter, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchChartData();
    fetchCashFlowData();
  }, [fetchChartData, fetchCashFlowData]);

  const statCards = [
    { 
      icon: Users, 
      label: 'Total de Clientes', 
      value: stats?.total_clients || 0, 
      color: 'bg-blue-500',
      link: '/admin/clients'
    },
    { 
      icon: FileText, 
      label: 'Documentos', 
      value: stats?.total_documents || 0, 
      color: 'bg-green-500',
      link: '/admin/documents'
    },
    { 
      icon: FileSignature, 
      label: 'Contratos Ativos', 
      value: stats?.active_contracts || 0, 
      color: 'bg-purple-500',
      link: '/admin/contracts'
    },
    { 
      icon: TrendingUp, 
      label: 'Contratos Pendentes', 
      value: stats?.pending_contracts || 0, 
      color: 'bg-yellow-500',
      link: '/admin/contracts'
    },
  ];

  const COLORS = {
    paid: '#22c55e',
    pending: '#eab308',
    overdue: '#ef4444'
  };

  const pieData = chartData?.totals ? [
    { name: 'Pago', value: chartData.totals.paid, color: COLORS.paid },
    { name: 'Pendente', value: chartData.totals.pending, color: COLORS.pending },
    { name: 'Em Atraso', value: chartData.totals.overdue, color: COLORS.overdue }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600 text-sm md:text-base">Bem-vindo(a), {admin?.full_name}!</p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.label}
                to={stat.link}
                className="bg-white rounded-lg shadow-md p-4 md:p-6 border-2 border-gray-200 hover:border-arcano-blue transition-all"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                  <div className={`${stat.color} p-2 md:p-3 rounded-lg`}>
                    <Icon size={24} className="text-white md:w-8 md:h-8" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-xs md:text-sm">{stat.label}</p>
                    <p className="text-xl md:text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Chart Section */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 border-2 border-gray-200">
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="text-arcano-blue" />
              Valor dos Contratos Ativos
            </h2>
            <p className="text-gray-600 text-xs md:text-sm">Parcelas pagas, pendentes e em atraso</p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-500 hidden sm:block" />
              
              {/* View Mode Filter (Monthly/Yearly) */}
              <div className="flex rounded-md overflow-hidden border border-gray-300">
                <button
                  onClick={() => setViewMode('monthly')}
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium transition-colors ${
                    viewMode === 'monthly'
                      ? 'bg-arcano-blue text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Personalizado
                </button>
                <button
                  onClick={() => setViewMode('yearly')}
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium transition-colors ${
                    viewMode === 'yearly'
                      ? 'bg-arcano-blue text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Anual
                </button>
              </div>
            </div>
            
            {viewMode === 'monthly' ? (
              /* Custom Date Range */
              <div className="flex flex-wrap items-center gap-2">
                <Calendar size={18} className="text-gray-500 hidden sm:block" />
                <input
                  type="text"
                  value={startDateDisplay}
                  onChange={handleStartDateChange}
                  placeholder="DD/MM/AAAA"
                  maxLength={10}
                  className="w-28 px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent text-xs md:text-sm text-center"
                />
                <span className="text-gray-500 text-sm">até</span>
                <input
                  type="text"
                  value={endDateDisplay}
                  onChange={handleEndDateChange}
                  placeholder="DD/MM/AAAA"
                  maxLength={10}
                  className="w-28 px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent text-xs md:text-sm text-center"
                />
              </div>
            ) : (
              /* Year Filter */
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(Number(e.target.value))}
                className="px-3 md:px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent text-xs md:text-sm"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {chartLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Totals Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className="bg-green-50 p-3 md:p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-xs md:text-sm text-green-800">Pago</span>
                </div>
                <p className="text-base md:text-xl font-bold text-green-700">{formatCurrency(chartData?.totals?.paid || 0)}</p>
              </div>
              <div className="bg-yellow-50 p-3 md:p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={16} className="text-yellow-600" />
                  <span className="text-xs md:text-sm text-yellow-800">Pendente</span>
                </div>
                <p className="text-base md:text-xl font-bold text-yellow-700">{formatCurrency(chartData?.totals?.pending || 0)}</p>
              </div>
              <div className="bg-red-50 p-3 md:p-4 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={16} className="text-red-600" />
                  <span className="text-xs md:text-sm text-red-800">Em Atraso</span>
                </div>
                <p className="text-base md:text-xl font-bold text-red-700">{formatCurrency(chartData?.totals?.overdue || 0)}</p>
              </div>
              <div className="bg-blue-50 p-3 md:p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign size={16} className="text-blue-600" />
                  <span className="text-xs md:text-sm text-blue-800">Total</span>
                </div>
                <p className="text-base md:text-xl font-bold text-blue-700">{formatCurrency(chartData?.totals?.total || 0)}</p>
              </div>
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Bar Chart */}
              <div className="lg:col-span-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Valores por Mês</h3>
                {chartData?.chart_data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis 
                        tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="paid" name="Pago" fill={COLORS.paid} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pending" name="Pendente" fill={COLORS.pending} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="overdue" name="Em Atraso" fill={COLORS.overdue} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    <p>Nenhum dado disponível para o período selecionado</p>
                  </div>
                )}
              </div>

              {/* Pie Chart */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Distribuição Total</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    <p>Nenhum dado disponível</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cash Flow Section */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 border-2 border-gray-200" data-testid="cash-flow-section">
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet className="text-arcano-blue" />
              Fluxo de Caixa
            </h2>
            <p className="text-gray-600 text-xs md:text-sm">Entradas (parcelas pagas) vs Saídas (despesas) com saldo acumulado</p>
          </div>
        </div>

        {cashFlowLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Cash Flow Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
              <div className="bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet size={16} className="text-gray-600" />
                  <span className="text-xs md:text-sm text-gray-600">Saldo Inicial</span>
                </div>
                <p className={`text-base md:text-xl font-bold ${(cashFlowData?.summary?.opening_balance || 0) >= 0 ? 'text-gray-700' : 'text-red-600'}`} data-testid="opening-balance">
                  {formatCurrency(cashFlowData?.summary?.opening_balance || 0)}
                </p>
              </div>
              <div className="bg-green-50 p-3 md:p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpCircle size={16} className="text-green-600" />
                  <span className="text-xs md:text-sm text-green-800">Entradas</span>
                </div>
                <p className="text-base md:text-xl font-bold text-green-700" data-testid="total-income">
                  {formatCurrency(cashFlowData?.summary?.total_income || 0)}
                </p>
              </div>
              <div className="bg-red-50 p-3 md:p-4 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDownCircle size={16} className="text-red-600" />
                  <span className="text-xs md:text-sm text-red-800">Saídas</span>
                </div>
                <p className="text-base md:text-xl font-bold text-red-700" data-testid="total-expenses">
                  {formatCurrency(cashFlowData?.summary?.total_expenses || 0)}
                </p>
              </div>
              <div className={`p-3 md:p-4 rounded-lg border ${(cashFlowData?.summary?.net_change || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {(cashFlowData?.summary?.net_change || 0) >= 0 ? (
                    <TrendingUp size={16} className="text-green-600" />
                  ) : (
                    <TrendingDown size={16} className="text-red-600" />
                  )}
                  <span className={`text-xs md:text-sm ${(cashFlowData?.summary?.net_change || 0) >= 0 ? 'text-green-800' : 'text-red-800'}`}>Resultado</span>
                </div>
                <p className={`text-base md:text-xl font-bold ${(cashFlowData?.summary?.net_change || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="net-change">
                  {formatCurrency(cashFlowData?.summary?.net_change || 0)}
                </p>
              </div>
              <div className="bg-blue-50 p-3 md:p-4 rounded-lg border border-blue-200 col-span-2 lg:col-span-1">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign size={16} className="text-blue-600" />
                  <span className="text-xs md:text-sm text-blue-800">Saldo Final</span>
                </div>
                <p className={`text-base md:text-xl font-bold ${(cashFlowData?.summary?.closing_balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="closing-balance">
                  {formatCurrency(cashFlowData?.summary?.closing_balance || 0)}
                </p>
              </div>
            </div>

            {/* Cash Flow Chart */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Fluxo de Caixa por Mês</h3>
              {cashFlowData?.chart_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={cashFlowData.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CashFlowTooltip />} />
                    <Legend />
                    <Bar dataKey="income" name="Entradas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Line 
                      type="monotone" 
                      dataKey="closing_balance" 
                      name="Saldo" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[350px] text-gray-500">
                  <div className="text-center">
                    <Wallet size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>Nenhum dado de fluxo de caixa disponível</p>
                    <Link to="/admin/expenses" className="text-arcano-blue hover:underline text-sm mt-2 inline-block">
                      Cadastrar despesas
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Link to Expenses */}
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
              <Link 
                to="/admin/expenses" 
                className="text-arcano-blue hover:underline text-sm flex items-center gap-1"
                data-testid="view-expenses-link"
              >
                <Wallet size={16} />
                Gerenciar Despesas
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Ações Rápidas</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/admin/clients/new"
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
          >
            <UserPlus size={24} className="text-arcano-blue" />
            <div>
              <p className="font-semibold text-gray-900">Novo Cliente</p>
              <p className="text-sm text-gray-600">Cadastrar cliente</p>
            </div>
          </Link>
          <Link
            to="/admin/contracts/new"
            className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
          >
            <FilePlus size={24} className="text-arcano-blue" />
            <div>
              <p className="font-semibold text-gray-900">Novo Contrato</p>
              <p className="text-sm text-gray-600">Criar contrato</p>
            </div>
          </Link>
          {admin?.is_super_admin && (
            <Link
              to="/admin/admins/new"
              className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-md hover:border-arcano-blue transition-colors"
            >
              <UserPlus size={24} className="text-arcano-blue" />
              <div>
                <p className="font-semibold text-gray-900">Novo Admin</p>
                <p className="text-sm text-gray-600">Adicionar administrador</p>
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-r from-arcano-blue to-blue-600 rounded-lg shadow-md p-8 text-white">
        <h2 className="text-2xl font-bold mb-2">Gandalf</h2>
        <p className="text-blue-100">
          Gerencie clientes, documentos e contratos da Arcano Projetos. Use o menu lateral para navegar entre as diferentes áreas do sistema.
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
